import React from "react";
import { ImageOff, RefreshCw, Search } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { Switch } from "components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { cn } from "@/lib/utils";

import {
  type BannerAspect,
  CAMPAIGN_TYPE_LABELS,
  CampaignType,
  type IBannerGalleryItem,
} from "@/@types/gamification";

import {
  BANNER_ASPECT_CLASSES,
  BANNER_ASPECT_RATIO_LABELS,
  BANNER_SOURCE_BADGES,
  GALLERY_ALL_GAMES,
  GALLERY_SOURCE_FILTER_OPTIONS,
  type GallerySourceFilter,
} from "../constants/banner-gallery";
import { BANNER_GALLERY_COPY } from "../constants/messages";
import { galleryGameLabel, galleryItemLabel } from "../utils/banner-source";
import {
  type GalleryGameFilter,
  useBannerGallery,
  type UseBannerGalleryResult,
} from "./use-banner-gallery";

const SKELETON_COUNT = 6;
const SEARCH_INPUT_ID = "banner-gallery-search";
const ASPECT_SWITCH_ID = "banner-gallery-all-aspects";

const GAME_FILTER_OPTIONS: ReadonlyArray<{ value: GalleryGameFilter; label: string }> = [
  { value: GALLERY_ALL_GAMES, label: BANNER_GALLERY_COPY.allGames },
  ...Object.values(CampaignType).map((type) => ({
    value: type,
    label: CAMPAIGN_TYPE_LABELS[type],
  })),
];

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

interface BannerGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aspect: BannerAspect;
  campaignType?: CampaignType;
  onSelect: (item: IBannerGalleryItem) => void;
}

const GameFilter: React.FC<{
  value: GalleryGameFilter;
  onChange: (value: GalleryGameFilter) => void;
}> = ({ value, onChange }) => (
  <div role="group" aria-label={BANNER_GALLERY_COPY.gameFilterLabel} className="flex flex-wrap gap-2">
    {GAME_FILTER_OPTIONS.map((option) => (
      <Button
        key={option.value}
        type="button"
        size="sm"
        variant={option.value === value ? "default" : "outline"}
        aria-pressed={option.value === value}
        className={cn("min-h-11 rounded-full", FOCUS_RING)}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </Button>
    ))}
  </div>
);

const GalleryCard: React.FC<{
  item: IBannerGalleryItem;
  onSelect: (item: IBannerGalleryItem) => void;
}> = ({ item, onSelect }) => {
  const label: string = galleryItemLabel(item);
  const game: string | null = galleryGameLabel(item.campaignType);
  const ratio: string = BANNER_ASPECT_RATIO_LABELS[item.aspect];

  return (
    <li>
      <button
        type="button"
        aria-label={BANNER_GALLERY_COPY.selectItem(label, ratio)}
        onClick={() => onSelect(item)}
        className={cn(
          "flex min-h-11 w-full flex-col gap-2 rounded-lg border bg-white p-2 text-left transition-colors hover:border-primary hover:bg-primary/5",
          FOCUS_RING,
        )}
      >
        <span
          className={cn(
            "block w-full overflow-hidden rounded-md bg-gray-100",
            BANNER_ASPECT_CLASSES[item.aspect],
          )}
        >
          <img
            src={item.url}
            alt={BANNER_GALLERY_COPY.imageAlt(label)}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </span>
        <span className="flex flex-wrap gap-1">
          <Badge variant="secondary">{BANNER_SOURCE_BADGES[item.source]}</Badge>
          {game && <Badge variant="outline">{game}</Badge>}
          <Badge variant="outline">{ratio}</Badge>
        </span>
        <span className="line-clamp-2 text-sm font-medium text-foreground">{label}</span>
      </button>
    </li>
  );
};

const GalleryGrid: React.FC<{
  gallery: UseBannerGalleryResult;
  onSelect: (item: IBannerGalleryItem) => void;
}> = ({ gallery, onSelect }) => {
  if (gallery.isError) {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-sm text-red-600">{BANNER_GALLERY_COPY.error}</p>
        <Button type="button" variant="outline" className={cn("min-h-11", FOCUS_RING)} onClick={gallery.retry}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          {BANNER_GALLERY_COPY.retry}
        </Button>
      </div>
    );
  }

  if (gallery.isLoading) {
    return (
      <div role="status" aria-label={BANNER_GALLERY_COPY.loading} className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <Skeleton key={index} className="aspect-[16/9] w-full" />
        ))}
      </div>
    );
  }

  if (gallery.items.length === 0) {
    return (
      <div role="status" className="flex flex-col items-center gap-2 py-10 text-center">
        <ImageOff className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium">{BANNER_GALLERY_COPY.empty}</p>
        <p className="text-xs text-muted-foreground">{BANNER_GALLERY_COPY.emptyHint}</p>
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {gallery.items.map((item) => (
        <GalleryCard key={item.key} item={item} onSelect={onSelect} />
      ))}
    </ul>
  );
};

const GalleryBody: React.FC<{
  aspect: BannerAspect;
  campaignType?: CampaignType;
  onSelect: (item: IBannerGalleryItem) => void;
}> = ({ aspect, campaignType, onSelect }) => {
  const gallery: UseBannerGalleryResult = useBannerGallery({
    aspect,
    initialCampaignType: campaignType,
  });

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <div className="space-y-2">
        <Label htmlFor={SEARCH_INPUT_ID}>{BANNER_GALLERY_COPY.searchLabel}</Label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            id={SEARCH_INPUT_ID}
            type="search"
            value={gallery.search}
            placeholder={BANNER_GALLERY_COPY.searchPlaceholder}
            onChange={(event) => gallery.setSearch(event.target.value)}
            className="h-11 pl-9"
          />
        </div>
      </div>

      <GameFilter value={gallery.gameFilter} onChange={gallery.setGameFilter} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={ASPECT_SWITCH_ID} className="flex min-h-11 cursor-pointer items-center gap-2 text-sm">
          <Switch
            id={ASPECT_SWITCH_ID}
            checked={gallery.showAllAspects}
            onCheckedChange={gallery.setShowAllAspects}
          />
          {BANNER_GALLERY_COPY.showAllAspects}
        </label>
        {!gallery.showAllAspects && (
          <span className="text-xs text-muted-foreground">
            {BANNER_GALLERY_COPY.onlyAspect(BANNER_ASPECT_RATIO_LABELS[aspect])}
          </span>
        )}
      </div>

      <Tabs
        value={gallery.sourceFilter}
        onValueChange={(value) => gallery.setSourceFilter(value as GallerySourceFilter)}
        className="flex min-h-0 flex-col"
      >
        <TabsList aria-label={BANNER_GALLERY_COPY.sourceTabsLabel} className="h-auto w-full justify-start gap-1 overflow-x-auto bg-muted p-1">
          {GALLERY_SOURCE_FILTER_OPTIONS.map((option) => (
            <TabsTrigger key={option.value} value={option.value} className="min-h-11 px-4">
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {GALLERY_SOURCE_FILTER_OPTIONS.map((option) => (
          <TabsContent key={option.value} value={option.value} className="max-h-[50vh] overflow-y-auto pr-1">
            <GalleryGrid gallery={gallery} onSelect={onSelect} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export const BannerGalleryDialog: React.FC<BannerGalleryDialogProps> = ({
  open,
  onOpenChange,
  aspect,
  campaignType,
  onSelect,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-3xl flex-col">
      <DialogHeader>
        <DialogTitle>{BANNER_GALLERY_COPY.title}</DialogTitle>
        <DialogDescription>{BANNER_GALLERY_COPY.description}</DialogDescription>
      </DialogHeader>
      {open && (
        <GalleryBody aspect={aspect} campaignType={campaignType} onSelect={onSelect} />
      )}
    </DialogContent>
  </Dialog>
);
